import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { EntrataApplicationStatus, PageType } from '@common/entities';
import { queryClient } from 'AppWrapper';
import { setApplicationData } from 'state/slices/applicationSlice';
import { setIsPreviewMode } from 'state/slices/authSlice';
import { updateResponseState } from 'state/slices/responseSlice';

import { useInitializeLJApplication } from 'hooks/useInitializeLJApplication';
import { getApplicationForm, getResponse } from 'services';
import {
  ApplicationStatusResponse,
  getApplicantApplications,
  isApplicationActive,
} from 'services/applicant';
import { Applicant } from 'types/applicant';
import { Form } from 'types/form';
import { prepareFormInitializingData } from 'utils/form';

const useAuthRedirection = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { pageId: currentPageId } = useParams();
  const initializeLJApplication = useInitializeLJApplication();

  const isPageIdAvailable = (pageId: string, { pages }: Form) => {
    if (!pageId) return false;
    const isAvailable = pages.findIndex((page) => page.id === pageId);
    return isAvailable >= 0;
  };

  const getPageIdForRedirection = (response: string, form: Form) => {
    // First priority: handle Offers page navigation
    if (form.pages && form.pages[0]?.type === PageType.Offers) {
      const offersPageId = form.pages[0].id;

      // If we're already on the Offers page, go to the next page
      if (currentPageId === offersPageId) {
        const nextPage = form.pages[1]; // Second page in the form
        if (nextPage && isPageIdAvailable(nextPage.id, form)) {
          return nextPage.id;
        }
      }

      // If we're not on the Offers page, go to it
      if (currentPageId !== offersPageId) {
        return offersPageId;
      }
    }

    // Second priority: go to the active page from response
    const parseResponse = JSON.parse(response);
    const { pages } = parseResponse;
    const activePage = pages.find((page: any) => page.isCurrentPage);
    if (isPageIdAvailable(activePage?.pageId, form)) return activePage.pageId;

    // Fallback: return first page
    return form.pages[0].id;
  };

  const getLatestApplicantResponse = async (responseId: string, viewerId?: string) => {
    try {
      const { data } = await queryClient.fetchQuery({
        queryKey: ['applicant-response', responseId, viewerId],
        queryFn: () => getResponse(responseId, viewerId),
      });
      return data;
    } catch (e: any) {
      throw new Error(e.message);
    }
  };

  const loadResponse = async (responseId: string, viewerId?: string) => {
    try {
      const response = await getLatestApplicantResponse(responseId, viewerId);
      const formValue = prepareFormInitializingData(response);
      dispatch(updateResponseState(formValue));
      return response?.details;
    } catch (err: any) {
      throw new Error(err.message);
    }
  };

  const getResponseIdToInitialize = (
    applicantApplications: ApplicationStatusResponse[],
    applicant: Applicant,
    shouldInitializeNewApplication = false,
  ): string | undefined => {
    if (shouldInitializeNewApplication) {
      return applicant.currentResponse?.responseId;
    }

    const activeApplications = applicantApplications.filter((application) =>
      isApplicationActive(application),
    );

    if (activeApplications.length === 1) {
      const activeApplication = activeApplications[0];

      // Only allow direct application resume if submissionStatus is NULL
      // If submissionStatus has any value (Submitting, Completed, Failed), redirect to Entry page
      if (
        activeApplication.submissionStatus !== null &&
        activeApplication.submissionStatus !== undefined
      ) {
        return undefined;
      }

      // Only resume is all other applications are either "cancelled" or "expired"
      const allOtherApplicationsValid = applicantApplications.every(
        (application) =>
          isApplicationActive(application) ||
          application.applicationStatus === EntrataApplicationStatus.Cancelled ||
          application.applicationStatus === EntrataApplicationStatus.Expired,
      );

      if (allOtherApplicationsValid) {
        return activeApplication.responseId;
      }
    }

    return undefined;
  };

  const initializeAppAndRedirect = async (
    responseId: string,
    userResponse: string,
    form: Form,
    applicant: Applicant,
    orgId: number,
    shouldInitializeNewApplication = false,
    afterNewApplicationInitialization?: () => Promise<void>,
  ) => {
    dispatch(setApplicationData({ applicant, form }));
    await initializeLJApplication({ applicant, form, responseId });

    if (shouldInitializeNewApplication && afterNewApplicationInitialization) {
      await afterNewApplicationInitialization();
    }

    dispatch(setIsPreviewMode(false));

    const route = shouldInitializeNewApplication
      ? form.pages[0].id
      : getPageIdForRedirection(userResponse, form);
    const navigationPath = `/${orgId}/${applicant.propertyId}/${route}`;
    navigate(navigationPath);
  };

  const getApplicantApplication = async (viewerId?: string) => {
    try {
      const { data } = await queryClient.fetchQuery({
        queryKey: ['applicant-application', viewerId],
        queryFn: () => getApplicantApplications(viewerId),
      });

      return data;
    } catch (e: any) {
      throw new Error(e?.message);
    }
  };

  const authRedirection = async (
    form: Form,
    applicant: Applicant,
    orgId: number,
    options?: {
      shouldInitializeNewApplication?: boolean;
      afterNewApplicationInitialization?: () => Promise<void>;
    },
  ) => {
    try {
      const params = new URLSearchParams(window.location.search);
      const attemptId = params.get('paymentAttempt');
      const operation = params.get('op');
      if (
        window.location.pathname.endsWith('/viewer-login') &&
        attemptId &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(attemptId) &&
        (operation === null || operation === 'success' || operation === 'cancel')
      ) {
        // The API checks the newly authenticated applicant's ownership and restores the saved route.
        // This endpoint only returns to verification; it never initiates Checkout.
        const suffix = operation ? `?op=${operation}` : '';
        window.location.replace(`/api/v1/viewer/payment-return/${attemptId}${suffix}`);
        return;
      }
      const shouldInitializeNewApplication = options?.shouldInitializeNewApplication === true;
      const applicantApplication = shouldInitializeNewApplication
        ? []
        : await getApplicantApplication(applicant.sid);

      const responseId = getResponseIdToInitialize(
        applicantApplication,
        applicant,
        shouldInitializeNewApplication,
      );
      if (!responseId) {
        dispatch(setApplicationData({ applicant, form }));
        dispatch(setIsPreviewMode(true));
        navigate(`/${orgId}/${applicant.propertyId}/entry`);
        return;
      }

      const responseContext = shouldInitializeNewApplication
        ? { applicant, form }
        : await getApplicationForm(responseId);
      const contextualApplicant = shouldInitializeNewApplication
        ? applicant
        : {
            ...responseContext.applicant,
            ...applicant,
            propertyId: responseContext.applicant.propertyId,
            responses: responseContext.applicant.responses,
            currentResponse: responseContext.applicant.currentResponse,
          };
      const userResponse = await loadResponse(responseId, contextualApplicant.sid);

      await initializeAppAndRedirect(
        responseId,
        userResponse as string,
        responseContext.form,
        contextualApplicant,
        orgId,
        shouldInitializeNewApplication,
        options?.afterNewApplicationInitialization,
      );
    } catch (err: any) {
      throw new Error(err?.message);
    }
  };

  return authRedirection;
};

export default useAuthRedirection;
